import type { RowSelectionState } from "@tanstack/react-table";
import { useCallback, useEffect, useRef, useState } from "react";

const getRowIdByIndex = (_: unknown, index: number) => String(index);

const hasSameRows = <TData>(previous: TData[], next: TData[]) =>
  previous.length === next.length &&
  previous.every((row, index) => row === next[index]);

interface UseDataTableRowDetailsOptions<TData> {
  data: TData[];
  /** Build a concise polite-announcement string for the focused row. */
  getAnnouncement?: (item: TData) => string;
}

interface UseDataTableRowDetailsOptionsWithDetail<
  TData,
> extends UseDataTableRowDetailsOptions<TData> {
  /** Pass `detail: true` to also receive `sheetOpen` and `onClose`. */
  detail: true;
}

interface UseDataTableRowDetailsResult<TData> {
  selectedIndex: number | null;
  selectedItem: TData | null;
  rowSelection: RowSelectionState;
  getRowId: (_: unknown, index: number) => string;
  onRowClick: (row: TData) => void;
  onRowFocus: (row: TData | null) => void;
  /** Debounced text for a polite live region; "" until a row is announced. */
  announcement: string;
}

interface UseDataTableRowDetailsResultWithDetail<
  TData,
> extends UseDataTableRowDetailsResult<TData> {
  sheetOpen: boolean;
  onClose: () => void;
}

/**
 * Master/detail state for a data grid using the WAI-ARIA "selection follows
 * focus" model. Pair with `<DataTable cellNavigation />`, which drives cell
 * navigation and reports the focused row via `onRowFocus`.
 *
 * The selected row is whichever data row currently holds focus, so the detail
 * shown is a pure function of focus: focusing a data row opens its detail,
 * focusing a header (null) closes it, moving across columns within a row leaves
 * it unchanged. Escape dismisses the detail until the focused row changes or a
 * row is activated again (click, or Enter on a cell).
 *
 * Cell navigation, the roving tabindex and scrolling the focused cell into view
 * are all handled natively by the browser / `useGridCellNavigation`, so this
 * hook holds no keyboard logic of its own. It is also safe to use alongside
 * multiple tables on the same page — unlike the previous global
 * `document.querySelector` approach, all state is scoped to the grid's own DOM.
 */
function useDataTableRowDetails<TData>(
  options: UseDataTableRowDetailsOptionsWithDetail<TData>
): UseDataTableRowDetailsResultWithDetail<TData>;

function useDataTableRowDetails<TData>(
  options: UseDataTableRowDetailsOptions<TData>
): UseDataTableRowDetailsResult<TData>;

function useDataTableRowDetails<TData>({
  data,
  getAnnouncement,
  ...rest
}: UseDataTableRowDetailsOptions<TData> & {
  detail?: true;
}):
  | UseDataTableRowDetailsResult<TData>
  | UseDataTableRowDetailsResultWithDetail<TData> {
  const hasDetail = "detail" in rest && rest.detail === true;

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  // Set when the detail is explicitly dismissed (Escape / close button) and
  // reset whenever the focused row changes, so dismissal doesn't survive
  // navigating to a different row.
  const [dismissed, setDismissed] = useState(false);

  // Reset selection when the page data changes (derived state during render,
  // avoids the extra render cycle of a useEffect).
  const [prevData, setPrevData] = useState(data);
  if (!hasSameRows(prevData, data)) {
    setPrevData(data);
    setSelectedIndex(null);
    setDismissed(false);
  }

  const selectedIndexRef = useRef<number | null>(selectedIndex);
  useEffect(() => {
    selectedIndexRef.current = selectedIndex;
  });

  const select = useCallback(
    (item: TData | null) => {
      if (item == null) {
        setSelectedIndex(null);
        return;
      }
      const index = data.indexOf(item);
      if (index === -1) return;
      if (selectedIndexRef.current !== index) setDismissed(false);
      setSelectedIndex(index);
    },
    [data]
  );

  // Debounced polite announcement of the focused row, so screen-reader users
  // learn what's now selected without re-reading the whole panel, and without
  // flooding announcements while arrowing quickly between rows.
  const [announcement, setAnnouncement] = useState("");
  const getAnnouncementRef = useRef(getAnnouncement);
  useEffect(() => {
    getAnnouncementRef.current = getAnnouncement;
  });
  useEffect(() => {
    const build = getAnnouncementRef.current;
    if (!build || selectedIndex === null) return;
    const item = data[selectedIndex];
    if (item == null) return;
    const id = setTimeout(() => setAnnouncement(build(item)), 250);
    return () => clearTimeout(id);
  }, [selectedIndex, data]);

  const activate = useCallback(
    (item: TData) => {
      setDismissed(false);
      select(item);
    },
    [select]
  );

  const onClose = useCallback(() => setDismissed(true), []);

  const base: UseDataTableRowDetailsResult<TData> = {
    selectedIndex,
    selectedItem: selectedIndex === null ? null : (data[selectedIndex] ?? null),
    rowSelection:
      selectedIndex === null ? {} : { [String(selectedIndex)]: true },
    getRowId: getRowIdByIndex,
    onRowClick: activate,
    onRowFocus: select,
    announcement,
  };

  if (hasDetail) {
    return {
      ...base,
      sheetOpen: selectedIndex !== null && !dismissed,
      onClose,
    } satisfies UseDataTableRowDetailsResultWithDetail<TData>;
  }

  return base;
}

export { useDataTableRowDetails };
export type {
  UseDataTableRowDetailsOptions,
  UseDataTableRowDetailsOptionsWithDetail,
  UseDataTableRowDetailsResult,
  UseDataTableRowDetailsResultWithDetail,
};
